import { useContext, useCallback } from 'react';
import { InternalError } from '../../../../../../Components/InternalError/InternalError';
import { IClientAssociationModel } from '../../../../../../Types/ClientModelTypes';
import { findCollectionStatic } from '../../../../../../Model/avenyStaticModelUtils';
import {
  IBusinessDataFormContext,
  BusinessDataFormContext,
} from '../BusinessDataForm/BusinessDataForm';
import IntegratedAssociationListItem from './IntegratedAssociationListItem';
import { INodeData } from '../../../../../../Components/QueryComponents/NodeQuery/NodeQuery';
import EDTypography from '../../../../../../BaseComponents/Typography/EDTypography';
import EDView from '../../../../../../BaseComponents/View/EDView';
import EDIcon from '../../../../../../BaseComponents/Icon/EDIcon';
import EDButton from '../../../../../../BaseComponents/Buttons/Button/EDButton';
import { makeStyles } from 'tss-react/mui';
import { getAssociationLabel } from '../../../../../../Model/BusinessModelUtils';
import { findBusinessColors } from '../../../Const/BusinessConfigs';
import { Theme } from '@mui/material/styles';

export interface IIntegratedContentName {
  name: string;
  association?: boolean;
  label?: string;
  mode?: 'edit' | 'read';
}

interface IProps<Associations> {
  associationName: Exclude<keyof Associations & string, '__typename'>;
  contentAttributeNames: readonly IIntegratedContentName[];
  labelAttributeNames: readonly IIntegratedContentName[];
  label: IIntegratedContentName;
  title?: string;
  expandable?: boolean;
}

export interface IOnDeleteIntegratedAssociation {
  id: string;
  _version: number;
}

const useStyles = makeStyles()(
  (theme: Theme) =>
    ({
      container: {
        display: 'flex',
        flexDirection: 'column',
        marginTop: 8,
      },
      newAssociationContainer: {
        display: 'flex',
        flexDirection: 'column-reverse',
      },
      tileContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(1),
      },
    } as const)
);

/**
 * Listar de associerade objekten för en "till många"-association.
 */
function IntegratedAssociationList<Associations>(props: IProps<Associations>) {
  const { classes } = useStyles();
  const formContext = useContext<IBusinessDataFormContext>(
    BusinessDataFormContext
  );
  const colors = findBusinessColors(props.associationName);

  const onUpdate = useCallback(
    (valueObject: INodeData, association?: string) => {
      if (association) {
        formContext?.update([
          {
            operation: 'Update_Values',
            associationName: props.associationName,
            id: valueObject.id,
          },
          {
            operation: 'Ref',
            associationName: association,
            id: valueObject[association],
          },
        ]);
      } else {
        formContext?.update([
          {
            operation: 'Update_Values',
            associationName: props.associationName,
            id: valueObject.id,
            values: valueObject,
          },
        ]);
      }
    },
    [props.associationName, formContext]
  );

  if (formContext == null) {
    return (
      <InternalError
        id="d8361c12-2d19-42cd-83f1-3d4c5e77c7cc"
        message=" IntegratedAssociationListutanför formcontext"
      />
    );
  }

  const onChange = (value: any, index: number, association?: string) => {
    if (formContext.data !== undefined) {
      if (association) {
        formContext.update([
          {
            operation: 'Add_Values',
            associationName: props.associationName,
            id: value.id,
          },
          {
            operation: 'Ref',
            associationName: association,
            id: value[association],
          },
        ]);
      } else {
        formContext.update([
          {
            operation: 'Add_Values',
            associationName: props.associationName,
            id: value.id,
            values: value,
          },
        ]);
      }
    }
  };

  const data: INodeData[] | undefined =
    formContext.data && formContext.data._Associations
      ? formContext.data._Associations[props.associationName]
      : undefined;

  const editedValues: INodeData[] | undefined =
    formContext.formState.values && formContext.formState.values._Associations
      ? formContext.formState.values._Associations[
          `${props.associationName}_Update_Values`
        ]
      : undefined;

  const addValues: INodeData[] =
    formContext.formState.values._Associations?.[
      `${props.associationName}_Add_Values`
    ] || [];

  const onDelete = (
    value: IOnDeleteIntegratedAssociation,
    _newIndex?: number
  ) => {
    formContext.update([
      {
        operation: 'Delete_Values',
        associationName: props.associationName,
        id: value.id,
      },
    ]);
  };

  const addNewAssociation = () => {
    formContext.update([
      {
        operation: 'Add_Values',
        associationName: props.associationName,
        id: `NewIntegratedAssociationListItem${addValues.length}`,
      },
    ]);
  };

  const collection = formContext.model;
  const association: IClientAssociationModel | undefined =
    collection && collection.Associations[props.associationName];

  const associatedCollection =
    association && findCollectionStatic(association.AssociatedCollectionName);

  if (association == null) {
    return (
      <InternalError
        message={`Kunde ej hitta association ${props.associationName}`}
        id={'b4362ff1-6ddd-46f1-9d94-7866000a71c3'}
      />
    );
  } else if (!associatedCollection) {
    return (
      <InternalError
        message={`Kunde ej hitta associerad collection ${association.AssociatedCollectionName}`}
        id={'b4362ff1-6ddd-46f1-9d94-7866000a71c3'}
      />
    );
  }

  return (
    <div className={classes.container}>
      <div className={classes.tileContainer}>
        <EDTypography bold variant="overline" color="textSecondary">
          {props.title}
        </EDTypography>
        {formContext.formState.editMode && (
          <EDButton
            icon={<EDIcon marginRight={1} iconType="add" color="primary" />}
            label={`Lägg till ${
              props.label.label ??
              getAssociationLabel(props.label, associatedCollection)
            }`}
            onClick={addNewAssociation}
            primary
            padding={1}
            flat
          />
        )}
      </div>
      {(!data || data.length === 0) && addValues.length === 0 && (
        <EDView gutter>
          <EDTypography>
            Det finns inga {associatedCollection.RootType.POLabel} att visa här
          </EDTypography>
        </EDView>
      )}
      <div className={classes.newAssociationContainer}>
        {addValues.map((addValuesData, index) => (
          <IntegratedAssociationListItem
            key={addValuesData.id}
            labelAttributeNames={props.labelAttributeNames}
            contentAttributeNames={props.contentAttributeNames}
            data={{ id: addValuesData.id, _version: 1 }}
            editedValues={{
              ...addValuesData,
              id: addValuesData.id,
              _version: 1,
            }}
            associatedCollection={associatedCollection}
            onUpdate={onUpdate}
            onNewUpdate={(value, association) =>
              onChange(value, index, association)
            }
            onDelete={value => onDelete(value, index)}
            associationName={association.AssociationName}
            label={props.label}
            expandable={props.expandable}
            disableDivider={
              (!data || data.length === 0) && addValues.length - 1 === index
            }
            colors={colors}
          />
        ))}
      </div>
      {data &&
        data.map((dataEl, idx) => (
          <IntegratedAssociationListItem
            key={dataEl.id}
            labelAttributeNames={props.labelAttributeNames}
            contentAttributeNames={props.contentAttributeNames}
            data={dataEl}
            editedValues={
              editedValues && editedValues.find(item => item.id === dataEl.id)
            }
            associatedCollection={associatedCollection}
            onUpdate={onUpdate}
            onDelete={onDelete}
            associationName={association.AssociationName}
            label={props.label}
            expandable={props.expandable}
            disableDivider={idx === data.length - 1}
            colors={colors}
          />
        ))}
    </div>
  );
}

export default IntegratedAssociationList;
